import * as d3 from "d3";
import { useEffect } from "react";
import { PEAD } from "../../../consts";
import type { SeriesDataItem } from "../../../types";
import { getColorForCurrentPead } from "../../helpers/getColorForCurrentPead/getColorForCurrentPead";

export function useCreateAllPeadChart(
	priceSeries: SeriesDataItem[],
	svgRef: React.RefObject<null>,
	maxXvalue: number,
	parentWidth = 928,
) {
	useEffect(() => {
		if (!priceSeries || priceSeries.length === 0) return;

		// Clear any existing SVG content
		d3.select(svgRef.current).selectAll("*").remove();

		// Specify the chart's dimensions.
		const width = parentWidth;
		const height = 500;
		const marginTop = 20;
		const marginRight = 20;
		const marginBottom = 30;
		const marginLeft = 60;

		const [minYValue, maxYValue] = d3.extent(
			priceSeries,
			(d) => d.normalizedPrice,
		);

		// Create the positional scales.
		const x = d3
			.scaleLinear()
			.domain([0, maxXvalue])
			.range([marginLeft, width - marginRight]);

		const y = d3
			.scaleLinear()
			.domain([Math.floor(minYValue || 0), Math.ceil(maxYValue || 200)])
			.nice()
			.range([height - marginBottom, marginTop]);
		// Create the SVG container.
		const svg = d3
			.select(svgRef.current)
			.attr("width", "100%")
			.attr("height", height)
			.attr("viewBox", [0, 0, width, height])
			.attr(
				"style",
				"width: 100%; height: auto; max-height: 600px; overflow: visible; font-size: 20px sans-serif; background-color: var(--background);",
			);

		// Add the horizontal axis.
		svg
			.append("g")
			.attr("transform", `translate(0,${height - marginBottom})`)
			.call(
				d3
					.axisBottom(x)
					.ticks(width / 50)
					.tickSizeOuter(0),
			)
			.call((g) => g.selectAll(".tick text").attr("font-size", "20px"));

		// Add the vertical axis.
		svg
			.append("g")
			.attr("transform", `translate(${marginLeft},0)`)
			.call(d3.axisLeft(y))
			.call((g) => g.select(".domain").remove())
			.call((g) => g.selectAll(".tick text").attr("font-size", "20px"))
			.call(
				(g) =>
					g
						.selectAll(".tick line")
						.clone()
						.attr("x2", width - marginLeft - marginRight)
						.attr("stroke-opacity", 0), // Hide the horizontal grid lines
			)
			.call((g) =>
				g
					.append("text")
					.attr("x", marginLeft * 0.5)
					.attr("y", 30)
					.attr("fill", "currentColor")
					.attr("text-anchor", "start")
					.attr("font-size", "24px")
					.text("↑ price (%)"),
			)
			.call(
				(g) =>
					g
						.append("text")
						.attr("x", marginLeft * 4)
						.attr("y", 30)
						.attr("fill", PEAD.CURRENT_PEAD_COLOR)
						.attr("text-anchor", "start")
						.attr("font-size", "24px")
						.text("GOLD: Current Quarter"), // legend for the current quarter
			)
			.call(
				(g) =>
					g
						.append("text")
						.attr("x", marginLeft * 10)
						.attr("y", 30)
						.attr("fill", PEAD.LAST_4_QTR_COLOR)
						.attr("text-anchor", "start")
						.attr("font-size", "24px")
						.text("BLUE: Last 4 Quarters"), // legend for the last 4 quarters
			);

		// Compute the points in pixel space as [x, y, z], where z is the name of the series.
		const points: [number, number, string][] = priceSeries.map((d) => [
			x(new Date(d.date)),
			y(d.normalizedPrice as number),
			d.division,
		]);

		// Group the points by series.
		const groups = d3.rollup(
			points,
			(v) => Object.assign(v, { z: v[0][2] }),
			(d) => d[2],
		);

		// Draw the lines.
		const line = d3
			.line<[number, number]>()
			.x((d) => d[0])
			.y((d) => d[1]);

		const path = svg
			.append("g")
			.attr("fill", "none")
			.style("stroke", PEAD.DEFAULT_PEAD_COLOR)
			.attr("stroke-width", 3)
			.attr("stroke-linejoin", "round")
			.attr("stroke-linecap", "round")
			.selectAll("path")
			.data(groups.values())
			.join("path")
			.style("stroke", (d) => getColorForCurrentPead(d, points))
			.attr("d", (d) => line(d.map((p) => [+p[0], +p[1]] as [number, number])));

		const dot = svg.append("g").attr("display", "none"); // label for the interactive tip

		dot
			.append("rect")
			.attr("width", 160)
			.attr("height", 40)
			.attr("fill", "var(--background)");

		dot
			.append("text")
			.attr("x", 80) // center of rect (width/2)
			.attr("y", 20) // middle of rect (height/2)
			.attr("text-anchor", "middle")
			.attr("fill", "var(--light-gray)")
			.attr("dominant-baseline", "middle");

		dot.append("circle").attr("r", 2);

		dot.append("text").attr("text-anchor", "middle").attr("y", -8);

		// When the pointer moves, find the closest point, update the interactive tip, and highlight
		// the corresponding line.
		function pointerMoved(event: MouseEvent) {
			const [xm, ym] = d3.pointer(event);
			const i = d3.leastIndex(points, ([x, y]: [number, number, string]) =>
				Math.hypot(x - xm, y - ym),
			);

			if (i === undefined) return;

			const [x, y, k] = points[i];

			path
				.style("stroke", (d) =>
					d.z === k ? "red" : getColorForCurrentPead(d, points),
				)
				.filter(({ z }) => z === k);

			dot.attr("transform", `translate(${x},${y})`);

			dot
				.select("text")
				.text(`${k} ${Math.trunc(priceSeries[i].normalizedPrice || 0)}%`)
				.attr("fill", "black");

			dot.select("rect").attr("fill", getColorForCurrentPead({ z: k }, points));

			svg
				.property("value", priceSeries[i])
				.dispatch("input", { bubbles: true, cancelable: true, detail: null });
		}

		function pointerEntered() {
			path.style("mix-blend-mode", null);

			dot
				.attr("display", "block")
				.attr(
					"style",
					"font-size: 25px; padding: 5px; border-radius: 5px; color: white; font-weight: bold;",
				);
		}

		function pointerLeft() {
			dot.attr("display", "none");

			path
				.style("mix-blend-mode", null)
				.style("stroke", (d) => getColorForCurrentPead(d, points))
				.sort((a, b) => d3.ascending(+a.z, +b.z)); // Sort initially by z value

			const svgNode = svg.node();

			if (svgNode) {
				const node = svgNode as SVGSVGElement & { value: any };
				node.value = null;
			}

			svg.dispatch("input", { bubbles: true, cancelable: true, detail: null });
		}

		svg
			.on("pointerenter", pointerEntered)
			.on("pointermove", pointerMoved)
			.on("pointerleave", pointerLeft)
			.on("touchstart", (event) => event.preventDefault());
	}, [svgRef.current, priceSeries, maxXvalue, parentWidth]);
}
